import React, { useState } from 'react';
import { useDesign } from '../../designs';
import { saveLodging, deleteLodging } from '../../services/trips';
import { TripLodging } from '../../types/trip';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import DateSpinner from '../../components/ui/DateSpinner';
import TimeSpinner from '../../components/ui/TimeSpinner';
import DesignButton from '../../components/ui/DesignButton';

interface EditLodgingModalProps {
  tripId: string;
  lodging?: TripLodging;
  startDate: string;
  endDate: string;
  onClose: () => void;
}

export default function EditLodgingModal({
  tripId,
  lodging,
  startDate,
  endDate,
  onClose,
}: EditLodgingModalProps) {
  const [name, setName] = useState(lodging?.name || '');
  const [address, setAddress] = useState(lodging?.address || '');
  const [accessCode, setAccessCode] = useState(lodging?.accessCode || '');
  const [checkInDate, setCheckInDate] = useState(lodging?.checkInDate || startDate);
  const [checkInTime, setCheckInTime] = useState(lodging?.checkInTime || '');
  const [checkOutDate, setCheckOutDate] = useState(lodging?.checkOutDate || endDate);
  const [checkOutTime, setCheckOutTime] = useState(lodging?.checkOutTime || '');
  const [url, setUrl] = useState(lodging?.url || '');
  const [notes, setNotes] = useState(lodging?.notes || '');
  const [lat, setLat] = useState<number | undefined>(lodging?.lat);
  const [lng, setLng] = useState<number | undefined>(lodging?.lng);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Validate check-out after check-in
      if (checkInDate && checkOutDate && checkOutDate < checkInDate) {
        return;
      }

      const data: TripLodging = {
        name,
        address,
        ...(accessCode ? { accessCode } : {}),
        ...(checkInDate ? { checkInDate } : {}),
        ...(checkInTime ? { checkInTime } : {}),
        ...(checkOutDate ? { checkOutDate } : {}),
        ...(checkOutTime ? { checkOutTime } : {}),
        ...(url ? { url } : {}),
        ...(notes ? { notes } : {}),
        ...(lat != null ? { lat } : {}),
        ...(lng != null ? { lng } : {}),
      };
      await saveLodging(tripId, data);
      onClose();
    } catch (err) {
      console.error('Failed to save lodging:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete lodging? This cannot be undone.')) return;
    setIsSubmitting(true);
    try {
      await deleteLodging(tripId);
      onClose();
    } catch (err) {
      console.error('Failed to delete lodging:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title={lodging?.name ? 'Edit Lodging' : 'Add Lodging'} onClose={onClose}>
      <PlacesAutocomplete
        label="Name"
        value={name}
        onChange={setName}
        onPlaceSelect={(place) => {
          if (!address && place.address) setAddress(place.address);
          if (place.lat) setLat(place.lat);
          if (place.lng) setLng(place.lng);
        }}
      />
      <PlacesAutocomplete
        label="Address"
        value={address}
        onChange={setAddress}
        onPlaceSelect={(place) => {
          if (place.lat) setLat(place.lat);
          if (place.lng) setLng(place.lng);
        }}
      />
      <Input label="Access Code" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} />

      <div style={{ display: 'flex', gap: '16px' }}>
        <DateSpinner label="Check-in Date" value={checkInDate} onChange={setCheckInDate} />
        <TimeSpinner label="Time" value={checkInTime} onChange={setCheckInTime} />
      </div>

      <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
        <DateSpinner label="Check-out Date" value={checkOutDate} onChange={setCheckOutDate} min={checkInDate} />
        <TimeSpinner label="Time" value={checkOutTime} onChange={setCheckOutTime} />
      </div>

      <div style={{ marginTop: '16px' }}>
        <Input label="URL" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontFamily: "'Raleway', sans-serif",
          fontSize: fs.sectionHeader,
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: d.textSecondary,
          marginBottom: '6px',
        }}>
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontFamily: "'Raleway', sans-serif",
            fontSize: fs.bodyInput,
            fontWeight: 400,
            color: d.textPrimary,
            backgroundColor: d.bgSurface,
            border: '1px solid rgba(200,146,58,0.10)',
            borderRadius: '2px',
            outline: 'none',
            boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '16px' }}>
        {lodging?.name && (
          <button onClick={handleDelete} disabled={isSubmitting} type="button" style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
            letterSpacing: '1.5px', textTransform: 'uppercase',
            color: d.negative, backgroundColor: 'transparent',
            border: `1px solid ${d.negative}`, borderRadius: '2px',
            padding: '11px 16px', cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.5 : 1,
          }}>
            Delete
          </button>
        )}
        <DesignButton tier={2} fullWidth onClick={handleSave} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : lodging?.name ? 'Save' : 'Add Lodging'}
        </DesignButton>
      </div>
    </Modal>
  );
}
